import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { io, Socket } from 'socket.io-client';
import { authAPI } from '../api';

interface User {
  id: string;
  tenant_id: string | null;
  role: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  availability_status?: string;
}

interface AuthStore {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  loadCurrentUser: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const res = await authAPI.login(email, password);
          const { access_token, user } = res.data;
          localStorage.setItem('access_token', access_token);
          set({ user, accessToken: access_token });
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        try { await authAPI.logout(); } catch {}
        localStorage.removeItem('access_token');
        set({ user: null, accessToken: null });
        window.location.href = '/auth/login';
      },

      setUser: (user) => set({ user }),
      setToken: (token) => set({ accessToken: token }),

      loadCurrentUser: async () => {
        try {
          const res = await authAPI.me();
          set({ user: res.data });
        } catch {
          set({ user: null });
        }
      },
    }),
    {
      name: 'shispare-auth',
      partialize: (state) => ({ user: state.user, accessToken: state.accessToken }),
    }
  )
);

// ── SOCKET STORE ──────────────────────────────────────────────
interface SocketStore {
  socket: Socket | null;
  connected: boolean;
  connect: (token: string) => void;
  disconnect: () => void;
}

export const useSocketStore = create<SocketStore>((set, get) => ({
  socket: null,
  connected: false,

  connect: (token: string) => {
    if (get().socket?.connected) return;
    // Use env var baked in at build time — falls back to same origin
    const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const socket = io(socketUrl, {
      auth: { token },
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      transports: ['websocket', 'polling'],
    });
    socket.on('connect', () => set({ connected: true }));
    socket.on('disconnect', () => set({ connected: false }));
    set({ socket });
  },

  disconnect: () => {
    get().socket?.disconnect();
    set({ socket: null, connected: false });
  },
}));

// ── UI STORE ──────────────────────────────────────────────────
interface UIStore {
  sidebarOpen: boolean;
  notifications: any[];
  unreadCount: number;
  setSidebarOpen: (open: boolean) => void;
  addNotification: (notif: any) => void;
  setNotifications: (notifs: any[]) => void;
}

export const useUIStore = create<UIStore>((set) => ({
  sidebarOpen: true,
  notifications: [],
  unreadCount: 0,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  addNotification: (notif) =>
    set((state) => ({
      notifications: [notif, ...state.notifications].slice(0, 50),
      unreadCount: state.unreadCount + 1,
    })),
  setNotifications: (notifs) =>
    set({ notifications: notifs, unreadCount: notifs.filter((n: any) => !n.is_read).length }),
}));
